import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {Avatar, Form, Input, notification} from "antd";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import {InfoOutlined} from "@ant-design/icons";
import {IUserGroupDetails} from "@Models/IUserGroupDetails";
import UserGroupService from "@Services/UserGroupService";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {GroupContext, QueryKeys, SystemContext} from "@Constants";
import {MainContext} from "@Context/provider";
import {SkeletonRows} from "@Components/shared/SkeletonRows";
import {getSystemId, hasCapability} from "@Utils";
import InspectorModuleTitle from "@Components/groups/InspectorModuleTitle";
import InspectorInfoField from "@Components/groups/InspectorInfoField";
import SessionManager from "@Core/session";
import { ICheckCapFull } from "./types";

interface IProps {
    instance: string;
    checkCap: ICheckCapFull;
}

const ModuleGroup: React.FC<IProps> = ({instance, checkCap}: IProps) => {
    const {ui: uiState} = useContext(MainContext);
    const [data, setData] = useState<IUserGroupDetails>(null);
    const [fetching, setFetching] = useState<boolean>(false);
    const queryClient = useQueryClient();
    const stateRef = useRef<IUserGroupDetails>();
    stateRef.current = data;

    /**
     * Get all groups. This is required to update the capacity count field when membership changes.
     */
    const groupsQuery = useQuery<IUserGroupDetails[]>(
        [QueryKeys.UserGroups],
        async () => {
            const getSystem = hasCapability("UserGroup/GetSystem", SystemContext);
            const service = new UserGroupService();
            const groupsResult = getSystem ?
                await service.getSystem() :
                await service.getUser(SessionManager.getServiceUser()?.userId);

            if (!groupsResult.hasErrors) {
                return groupsResult.value;
            }
            return [];
        },
        {
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            enabled: !!SessionManager.getServiceUser()?.userId
        }
    );

    /**
     * Update the model with edited data.
     */
    const updateField = useMutation(
        async () => {
            setFetching(true);
            const service = new UserGroupService();
            await service.update(stateRef.current);
            setFetching(false);
        },
        {
            onSuccess: async () => {
                notification.success({message: "Group updated."});
                // Force the update in the groups list
                await queryClient.invalidateQueries([QueryKeys.UserGroups]);    
            }
        }
    );

    /**
     * Trigger an update two seconds after last keypress.
     */
    const debouncePromise = useCallback(AwesomeDebouncePromise(updateField.mutate, 2000), []);

    /**
     * Trigger an update to the form data state
     * @param field
     * @param value
     */
    const onInputChange = async (field, value) => {
        const formData = {...data};
        formData[field] = value;
        setData(formData);
        await debouncePromise();
    };

    /**
     * Call the check capability methods passed from props with the appropriate context and instance
     * @param cap
     */
    const allowed = (cap: string) => {
        return checkCap(cap, GroupContext, instance);
    };

    /**
     * Fetch current group data.
     */
    useEffect(() => {
        stateRef.current = null;
        setData(null);
        
        const group = groupsQuery.data?.find(x => x.groupId === instance);
        if (group !== null) {
            stateRef.current = group;
            if (stateRef.current !== null) {
                setData(stateRef.current);
            }
        }
    }, [uiState.currentGroup, groupsQuery.data]);
    
    const groupType = (data?.parentGroupId == getSystemId() ? "Root group" : (data?.capacity == -1 ? "Sub-group" : "License group"));

    return uiState.currentGroup === null || uiState.currentGroup.instance === getSystemId() ? null : (
        <>
            <div style={{padding: "12px 16px"}}>
                <InspectorModuleTitle
                    title={"Group Details"}
                    slug={"group"}
                    fetching={fetching}
                    icon={<InfoOutlined/>}
                />
                    {data === null ? <SkeletonRows rowHeight={40} count={1}/>    :
                        <Form
                            className="inspector-form"
                            initialValues={{}}
                        >
                            <Form.Item label="Group name"
                            >
                                {allowed("UserGroup/Update") ?
                                    <Input
                                        name={"name"}
                                        value={data?.name}
                                        onChange={e => onInputChange(e.target.name, e.target.value)}
                                    />
                                    :
                                    <InspectorInfoField>{data?.name}</InspectorInfoField>}
                            </Form.Item>
                            <Form.Item label={"Type"}>
                                <InspectorInfoField>{groupType}</InspectorInfoField>
                            </Form.Item>
                            <Form.Item label={"Users"}>
                                <InspectorInfoField color={data?.capacity > 0 && data?.count >= data?.capacity ? "yellow" : "blue"}>
                                    {data?.count.toString()}
                                    {data?.capacity > 0 && <>&nbsp;/&nbsp;{data?.capacity.toString()}</>}
                                </InspectorInfoField>
                            </Form.Item>
                        </Form>
                    }
                </div>
            <hr/>
        </>
    );
};
export default ModuleGroup;
